import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '@/store';
import { logout } from '../slices/authSlice';
import { setParlayEnabled } from '../slices/parlaySlice';
import { clearStoredParlaySelections } from '@/utils/parlaySelectionDb';

type CustomFetchArgs = FetchArgs & {
  requiresAuth?: boolean;
};

let redirectToLoginTimer: ReturnType<typeof setTimeout> | undefined;

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    const tempAuthToken = (getState() as RootState).auth.tempAuthToken;
    const finalToken = token || tempAuthToken;
    if (finalToken) {
      headers.set('authorization', `Bearer ${finalToken}`);
    }
    // headers.set('content-type', 'application/json');
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | CustomFetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;

  const token = state.auth.accessToken;
  const tempToken = state.auth.tempAuthToken;
  const finalToken = token || tempToken;

  const requiresAuth = typeof args !== 'string' && args.requiresAuth === true;

  if (requiresAuth && !finalToken) {
    return {
      error: {
        status: 401,
        data: {
          message: 'Authentication required',
        },
      },
    };
  }

  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    api.dispatch(logout());
    api.dispatch(setParlayEnabled(false));
    clearStoredParlaySelections();

    if (redirectToLoginTimer) {
      clearTimeout(redirectToLoginTimer);
    }

    redirectToLoginTimer = setTimeout(() => {
      redirectToLoginTimer = undefined;

      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }, 2000);
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: [
    'UserDetails',
    'Auth',
    'Competitions',
    'Events',
    'Bookset',
    'StakeSet',
    'BetHistory',
    'CurrentBetCount',
    'OneClickBetting',
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;

export default apiSlice;
